import { useState } from 'react';
import { toast } from 'sonner';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { MaskedInput } from '@/shared/components/masked-input';
import { maskPhone, maskCurrency } from '@/shared/lib/input-masks';
import { isValidEmail, isValidPhone } from '@/features/nps/public-survey/partition-questions';
import { ApiError } from '@/shared/api/client';
import { detectClientPlatform } from '@/shared/lib/device';
import {
  redeemGiftCard,
  type PublicInvitePassbook,
  type RedeemGiftCardResult,
} from '../api/use-public-invite';
import { PublicInviteTermsCheckbox } from './public-invite-terms-checkbox';
import { PublicInviteSubmittingOverlay } from './public-invite-submitting-overlay';
import { PublicInviteFormPanel } from './public-invite-form-panel';

interface PublicInviteGiftRedeemWizardProps {
  passbook: PublicInvitePassbook;
  /** trintaeum-livre deixa escolher o valor a carregar; trintaeum normal tem valor fixo no backend. */
  showValorGift: boolean;
  campanha?: string;
  onSuccess: (result: RedeemGiftCardResult) => void;
}

/**
 * Resgate de cartão-presente pré-carregado (`trintaeum`/`trintaeum-livre`) —
 * legacy/fixe-link-2 views/invite_trintaeum[_livre].ejs. NUNCA cria cadastro
 * novo: o campo "Cartão" é o código impresso no cartão físico que o usuário
 * já tem, não um CPF. Sem nascimento/gênero; email opcional; termos
 * pré-marcados, igual ao omatuto.
 */
export function PublicInviteGiftRedeemWizard({
  passbook,
  showValorGift,
  campanha,
  onSuccess,
}: PublicInviteGiftRedeemWizardProps) {
  const [cardCode, setCardCode] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [valorGift, setValorGift] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const handleFinish = async () => {
    if (!cardCode.trim()) {
      toast.error('Preencha o código do cartão.');
      return;
    }
    if (!name.trim()) {
      toast.error('Preencha seu nome.');
      return;
    }
    if (email.trim() && !isValidEmail(email)) {
      toast.error('Preencha um e-mail válido.');
      return;
    }
    if (!isValidPhone(phone)) {
      toast.error('Preencha seu celular com DDD.');
      return;
    }
    if (!termsAccepted) {
      toast.error('Obrigatório aceitar os termos e condições do regulamento.');
      return;
    }

    setSubmitting(true);
    setSucceeded(false);
    try {
      const platform = detectClientPlatform();
      const result = await redeemGiftCard(passbook.idPassbook, {
        uniqueId: cardCode.trim(),
        name: name.trim(),
        email: email.trim() || undefined,
        phone,
        platform,
        campanha,
        valorGift: showValorGift ? valorGift || undefined : undefined,
        website,
      });
      // "não encontrado" não é sucesso — só mostra o morfismo de check quando o cartão foi resgatado de fato.
      if (result.status === 'emailed') {
        setSucceeded(true);
        await new Promise((resolve) => setTimeout(resolve, 650));
      }
      onSuccess(result);
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : 'Não foi possível concluir o resgate. Tente novamente.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicInviteFormPanel
      onSubmit={handleFinish}
      submitting={submitting}
      submitLabel="Validar"
      submitLoadingLabel="Enviando..."
      submittingOverlay={<PublicInviteSubmittingOverlay succeeded={succeeded} />}
      accentColor={passbook.backgroundColor}
      foregroundColor={passbook.foregroundColor}
    >
      <div className="space-y-2">
        <Label htmlFor="gift-card-code">
          Cartão <span className="text-destructive">*</span>
        </Label>
        <Input
          id="gift-card-code"
          maxLength={11}
          style={{ textTransform: 'uppercase' }}
          placeholder="Preencha o código do cartão"
          value={cardCode}
          onChange={(e) => setCardCode(e.target.value.toUpperCase())}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="gift-name">
            Nome completo <span className="text-destructive">*</span>
          </Label>
          <Input
            id="gift-name"
            autoComplete="name"
            placeholder="Preencha seu nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="gift-email">Email</Label>
          <Input
            id="gift-email"
            type="email"
            autoComplete="email"
            placeholder="Preencha seu e-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="gift-phone">
            Celular <span className="text-destructive">*</span>
          </Label>
          <MaskedInput
            id="gift-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="Preencha seu celular com DDD"
            value={phone}
            onValueChange={setPhone}
            mask={maskPhone}
          />
        </div>
        {showValorGift ? (
          <div className="space-y-2">
            <Label htmlFor="gift-valor">Valor do Cartão</Label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-sm text-muted-foreground">
                R$
              </span>
              <MaskedInput
                id="gift-valor"
                inputMode="decimal"
                placeholder="0,00"
                className="pl-9"
                value={valorGift}
                onValueChange={setValorGift}
                mask={maskCurrency}
              />
            </div>
          </div>
        ) : null}
      </div>

      <PublicInviteTermsCheckbox
        checked={termsAccepted}
        onCheckedChange={setTermsAccepted}
        organizationName={passbook.organizationName}
        linkTermsConditions={passbook.linkTermsConditions}
        termsAndConditions={passbook.termsAndConditions}
        accentColor={passbook.backgroundColor}
      />

      <div className="sr-only" aria-hidden="true">
        <Label htmlFor="gift-website">Não preencha este campo</Label>
        <Input
          id="gift-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>
    </PublicInviteFormPanel>
  );
}
